import { createContext } from 'react';

/**
 * 🔴 A LIST'S FILTERS LIVE IN THE URL, AND THE KIT DOES NOT KNOW THE ROUTER — DS-10d (scope §3.2).
 *
 * `DocumentList` reads and writes its filters (status, from, to) through this state rather than its own `useState`, so
 * Back, a reload and a link sent to a colleague keep them. The state is the app's: the back office hands the kit its
 * `useListSearch` (api/useListSearch.ts — TanStack Router's search params, written with `replace: true`, never a
 * cursor) through this context, the same way it hands the kit its link (`RouteLinkContext`, route-link.tsx). Nothing
 * here imports the router (ui-gates.spec.ts), and a list drawn with no provider — a spec, a static render — keeps its
 * filters in its own state, as it did before DS-10d.
 *
 * - **The context holds the HOOK, not its answer.** A provider of the answer would have to read the URL at the shell
 *   and re-render the whole shell on every filter write; a hook is called by the list that draws the filters, and only
 *   that list re-renders. The provider's value never changes, so the list calls the same hook on every render.
 * - **A screen that owns an `extra` dimension** (Activities, Leads, PostDatedCheques) already holds the URL's state for
 *   it, and passes it whole as `DocumentList`'s `search` prop, so its controls and the list's read one URL.
 */

/** The URL's filters, as text: an absent key is no filter. */
export type ListSearchValues = Readonly<Partial<Record<string, string>>>;

/** A change to the filters. An empty value clears its key; a cursor is not a filter and the type refuses one. */
export type ListSearchPatch = Partial<Record<string, string>> & { after?: never; cursor?: never };

/** What `useListSearch` answers: the filters, and a writer that replaces them. */
export type ListSearchState = readonly [values: ListSearchValues, set: (patch: ListSearchPatch) => void];

/** The app's list-search hook (the back office's `useListSearch`), or `null` where there is no router. */
export const ListSearchContext = createContext<(() => ListSearchState) | null>(null);
